/**
 * Utility functions for handling embedded iframe context
 */

/**
 * Check if we're currently in an embedded context (iframe)
 */
export function isEmbeddedContext(): boolean {
  // Check if running in iframe
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access parent, we're likely in iframe with different origin
    return true;
  }
}

/**
 * Check if current route is an embedded route
 */
export function isEmbeddedRoute(path: string): boolean {
  return path.includes('/embed/');
}

/**
 * Whether the app should render embedded UI (iframe, /embed/ routes, or embed query params).
 * Keep in sync with WebAppLayout embedded detection (+ optional `embedded` prop there).
 */
export function isEmbeddedExperience(routeLike?: {
  path?: string;
  query?: Record<string, unknown>;
}): boolean {
  const path = routeLike?.path ?? '';
  const query = routeLike?.query ?? {};

  return isEmbeddedRoute(path)
    || isEmbeddedContext()
    || query.embedded === 'true'
    || !!query.embedOrganizationId;
}

/**
 * Handle payment success redirect for embedded context
 */
export function handlePaymentSuccessRedirect(amount: number, email?: string): void {
  const urlParams = new URLSearchParams(window.location.search);
  const isEmbedded = urlParams.get('embedded') === 'true' || isEmbeddedContext();

  if (isEmbedded) {
    // Try to get the referrer to redirect back to organization profile
    const { referrer } = document;

    if (referrer && (referrer.includes('/web/embed/org/') || referrer.includes('/web/embed/organization/'))) {
      // Redirect back to organization profile
      window.location.href = referrer;
    } else {
      // Post message to parent window to handle the success
      window.parent.postMessage({
        type: 'payment_success',
        amount,
        email,
      }, '*');

      // Close the payment window after a short delay
      setTimeout(() => {
        window.close();
      }, 2000);
    }
  } else {
    // Normal non-embedded flow
    const redirectUrl = window.location.origin;
    const store = (window as any).$store; // Access Vuex store if available

    if (store?.getters['users/me']) {
      const baseUrl = `${redirectUrl}/web/stickers/purchased`;
      const queryParams = `action=purchaseSticker&amount=${amount}&success=true`;
      window.location.href = `${baseUrl}?${queryParams}`;
    } else if (email) {
      const encodedEmail = encodeURIComponent(email);
      const profileUrl = `${redirectUrl}/auth/web/complete-profile?email=${encodedEmail}`;
      // Preserve embedded context when redirecting
      window.location.href = addEmbeddedContextToUrl(profileUrl);
    } else {
      window.location.href = `${redirectUrl}/web/`;
    }
  }
}

/**
 * Add embedded context to payment URL
 */
export function addEmbeddedContextToUrl(url: string): string {
  const urlObj = new URL(url, window.location.origin);
  const params = new URLSearchParams(urlObj.search);

  // Check if we're in embedded context
  if (isEmbeddedRoute(window.location.pathname) || isEmbeddedContext()) {
    params.set('embedded', 'true');

    // Add referrer information for proper redirect
    if (document.referrer) {
      params.set('referrer', encodeURIComponent(document.referrer));
    }

    // Extract and preserve embed parameters from current URL
    const currentUrl = new URL(window.location.href);
    const currentParams = new URLSearchParams(currentUrl.search);

    // Preserve embedOrganizationId if present
    if (currentParams.has('embedOrganizationId')) {
      const embedOrgId = currentParams.get('embedOrganizationId');
      if (embedOrgId) {
        params.set('embedOrganizationId', embedOrgId);
      }
    } else {
      // Try to extract from current path if it's an organization embed
      const embedOrgMatch = window.location.pathname.match(/\/embed\/organization\/([^/?]+)/);
      if (embedOrgMatch) {
        params.set('embedOrganizationId', embedOrgMatch[1]);
      }
    }

    // Preserve embedSource (embedHost) if present
    if (currentParams.has('embedSource')) {
      const embedSource = currentParams.get('embedSource');
      if (embedSource) {
        params.set('embedSource', embedSource);
      }
    }

    const standalone = currentParams.get('standalone');
    if (standalone === '1' || standalone === 'true') {
      params.set('standalone', '1');
    }

    const embedStickerId = currentParams.get('embedStickerId');
    if (embedStickerId) {
      params.set('embedStickerId', embedStickerId);
    } else {
      const stickerMatch = window.location.pathname.match(/\/embed\/sticker\/([^/?]+)/);
      if (stickerMatch) {
        params.set('embedStickerId', stickerMatch[1]);
      }
    }
  }

  urlObj.search = params.toString();
  return urlObj.toString();
}

/**
 * Keep embed navigation on the current origin so iframe height sync postMessages
 * reach the parent (which validates event.origin against embed base URL).
 */
export function resolveEmbeddedUrl(url: string): string {
  if (!isEmbeddedContext()) {
    return url;
  }

  try {
    const target = new URL(url, window.location.origin);
    const currentOrigin = window.location.origin;

    if (target.origin === currentOrigin) {
      return target.toString();
    }

    const resolved = `${currentOrigin}${target.pathname}${target.search}${target.hash}`;
    return resolved;
  } catch (error) {
    console.warn('Failed to rewrite embed navigation URL', { url, error });
    return url;
  }
}

/**
 * @deprecated Use resolveEmbeddedUrl — kept for existing call sites
 */
export function resolveEmbeddedPaymentUrl(url: string): string {
  return resolveEmbeddedUrl(url);
}

/**
 * Before a hard navigation to payment, lock a reasonable min-height on the parent.
 * Uses the visible content container — not body.scrollHeight (which tracks iframe size).
 */
export function prepareEmbedPaymentNavigation(): void {
  if (!isEmbeddedContext()) {
    return;
  }

  const EMBED_HEIGHT_KEY = 'spotlight-embed-last-height';
  const stored = parseInt(sessionStorage.getItem(EMBED_HEIGHT_KEY) || '0', 10) || 0;
  const contentEl = document.querySelector('.webStickerBody, .profile-container, main');
  const contentHeight = contentEl
    ? Math.ceil(contentEl.getBoundingClientRect().bottom + window.scrollY)
    : 0;
  const preservedHeight = Math.max(stored, contentHeight);

  sessionStorage.setItem(EMBED_HEIGHT_KEY, String(preservedHeight));

  window.parent.postMessage(
    {
      type: 'spotlight-iframe-min-height',
      minHeight: preservedHeight,
    },
    '*',
  );
}

/** Custom event dispatched when async content (e.g. Stripe) changes page height */
export const EMBED_HEIGHT_SYNC_EVENT = 'spotlight-embed-request-height';

/** Routes that should measure from actual content, not carry prior iframe height */
export function isCompactEmbedHeightRoute(path: string): boolean {
  return path.includes('/payment/')
    || path.includes('/web/checkout')
    || path.includes('/web/ticket/success')
    || path.includes('/auth/web/complete-profile')
    || path.includes('/web/embed/sticker/')
    || path.includes('/web/embed/organization/')
    || path.includes('/web/embed/org/');
}

/** Ask the embed height sync composable to remeasure (iframe context only) */
export function requestEmbedHeightSync(): void {
  if (!isEmbeddedContext()) {
    return;
  }

  window.dispatchEvent(new CustomEvent(EMBED_HEIGHT_SYNC_EVENT));
}

const EMBED_PATH_KEY = 'spotlight-embed-last-path';
const EMBED_ORG_CONTEXT_KEY = 'spotlight-embed-org-context';

export { EMBED_PATH_KEY, EMBED_ORG_CONTEXT_KEY };

export interface EmbedOrganizationContext {
  organizationId: string | null;
  embedSource: string | null;
  stickerId?: string | null;
  standalone?: boolean;
}

export function isStandaloneEmbedQuery(query: Record<string, unknown> = {}): boolean {
  return query.standalone === '1' || query.standalone === 'true';
}

function readQueryParamFromPath(fullPath: string, key: string): string | null {
  const queryIndex = fullPath.indexOf('?');
  if (queryIndex === -1) {
    return null;
  }

  return new URLSearchParams(fullPath.slice(queryIndex + 1)).get(key);
}

export function loadPersistedEmbedOrganizationContext(): EmbedOrganizationContext | null {
  try {
    const raw = sessionStorage.getItem(EMBED_ORG_CONTEXT_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as EmbedOrganizationContext;
    if (!parsed?.organizationId) {
      return null;
    }

    return {
      organizationId: parsed.organizationId,
      embedSource: parsed.embedSource || null,
      stickerId: parsed.stickerId || null,
      standalone: !!parsed.standalone,
    };
  } catch {
    return null;
  }
}

export function persistEmbedOrganizationContext(
  context: Partial<EmbedOrganizationContext>,
): void {
  if (!context.organizationId && !context.stickerId) {
    return;
  }

  const existing = loadPersistedEmbedOrganizationContext();
  const next: EmbedOrganizationContext = {
    organizationId: context.organizationId,
    embedSource: context.embedSource ?? existing?.embedSource ?? null,
    stickerId: context.stickerId ?? existing?.stickerId ?? null,
    standalone: context.standalone ?? existing?.standalone ?? false,
  };

  sessionStorage.setItem(EMBED_ORG_CONTEXT_KEY, JSON.stringify(next));
}

export function captureEmbedOrganizationContext(
  routeLike?: {
    query?: Record<string, unknown>;
    fullPath?: string;
    path?: string;
  },
): EmbedOrganizationContext {
  const resolved = resolveEmbedOrganizationContext(routeLike, { includePersisted: false });
  if (resolved.organizationId || resolved.stickerId) {
    persistEmbedOrganizationContext(resolved);
  }

  return resolveEmbedOrganizationContext(routeLike);
}

export function resolveEmbedOrganizationContext(
  routeLike?: {
    query?: Record<string, unknown>;
    fullPath?: string;
    path?: string;
  },
  options?: { includePersisted?: boolean },
): EmbedOrganizationContext {
  const includePersisted = options?.includePersisted !== false;
  const query = routeLike?.query || {};
  let organizationId = typeof query.embedOrganizationId === 'string'
    ? query.embedOrganizationId
    : null;
  let embedSource = typeof query.embedSource === 'string'
    ? query.embedSource
    : null;
  let stickerId = typeof query.embedStickerId === 'string'
    ? query.embedStickerId
    : null;
  let standalone = isStandaloneEmbedQuery(query);

  const path = routeLike?.fullPath || routeLike?.path || '';
  if (!organizationId) {
    const orgMatch = path.match(/\/embed\/organization\/([^/?]+)/);
    if (orgMatch) {
      organizationId = orgMatch[1];
    }
  }
  if (!stickerId) {
    const stickerMatch = path.match(/\/embed\/sticker\/([^/?]+)/);
    if (stickerMatch) {
      stickerId = stickerMatch[1];
    }
  }

  if (!embedSource) {
    embedSource = readQueryParamFromPath(path, 'embedSource');
  }
  if (!organizationId) {
    organizationId = readQueryParamFromPath(path, 'embedOrganizationId');
  }
  if (!stickerId) {
    stickerId = readQueryParamFromPath(path, 'embedStickerId');
  }
  if (!standalone) {
    standalone = readQueryParamFromPath(path, 'standalone') === '1'
      || readQueryParamFromPath(path, 'standalone') === 'true';
  }

  const redirectTarget = typeof query.redirect === 'string' ? query.redirect : null;
  if (redirectTarget) {
    if (!organizationId) {
      organizationId = readQueryParamFromPath(redirectTarget, 'embedOrganizationId');
    }
    if (!embedSource) {
      embedSource = readQueryParamFromPath(redirectTarget, 'embedSource');
    }
    if (!stickerId) {
      stickerId = readQueryParamFromPath(redirectTarget, 'embedStickerId');
    }
    if (!standalone) {
      standalone = readQueryParamFromPath(redirectTarget, 'standalone') === '1'
        || readQueryParamFromPath(redirectTarget, 'standalone') === 'true';
    }
  }

  const lastPath = sessionStorage.getItem(EMBED_PATH_KEY) || '';
  if (!organizationId) {
    const orgMatch = lastPath.match(/\/embed\/organization\/([^/?]+)/);
    if (orgMatch) {
      organizationId = orgMatch[1];
    }
  }
  if (!organizationId) {
    organizationId = readQueryParamFromPath(lastPath, 'embedOrganizationId');
  }
  if (!embedSource) {
    embedSource = readQueryParamFromPath(lastPath, 'embedSource');
  }
  if (!stickerId) {
    const stickerMatch = lastPath.match(/\/embed\/sticker\/([^/?]+)/);
    if (stickerMatch) {
      stickerId = stickerMatch[1];
    } else {
      stickerId = readQueryParamFromPath(lastPath, 'embedStickerId');
    }
  }
  if (!standalone) {
    standalone = readQueryParamFromPath(lastPath, 'standalone') === '1'
      || readQueryParamFromPath(lastPath, 'standalone') === 'true';
  }

  if (includePersisted) {
    const persisted = loadPersistedEmbedOrganizationContext();
    if (!organizationId && persisted?.organizationId) {
      organizationId = persisted.organizationId;
    }
    if (!embedSource && persisted?.embedSource) {
      embedSource = persisted.embedSource;
    }
    if (!stickerId && persisted?.stickerId) {
      stickerId = persisted.stickerId;
    }
    if (!standalone && persisted?.standalone) {
      standalone = persisted.standalone;
    }
  }

  return {
    organizationId,
    embedSource,
    stickerId,
    standalone,
  };
}

export function getEmbedOrganizationHomeUrl(
  contextOverride?: Partial<EmbedOrganizationContext>,
): string | null {
  const resolved = resolveEmbedOrganizationContext();
  const organizationId = contextOverride?.organizationId ?? resolved.organizationId;
  const embedSource = contextOverride?.embedSource ?? resolved.embedSource;

  if (!organizationId) {
    return null;
  }

  let url = `/web/embed/organization/${organizationId}`;
  if (embedSource) {
    url += `?embedSource=${encodeURIComponent(embedSource)}`;
  }

  return url;
}

export function getEmbedStickerHomeUrl(
  contextOverride?: Partial<EmbedOrganizationContext> & { donationSuccess?: boolean },
): string | null {
  const resolved = resolveEmbedOrganizationContext();
  const stickerId = contextOverride?.stickerId ?? resolved.stickerId;
  const organizationId = contextOverride?.organizationId ?? resolved.organizationId;
  const embedSource = contextOverride?.embedSource ?? resolved.embedSource;
  const standalone = contextOverride?.standalone ?? resolved.standalone;

  if (!stickerId) {
    return null;
  }

  const params = new URLSearchParams();
  if (organizationId) {
    params.set('embedOrganizationId', organizationId);
  }
  if (embedSource) {
    params.set('embedSource', embedSource);
  }
  if (standalone) {
    params.set('standalone', '1');
  }
  if (contextOverride?.donationSuccess) {
    params.set('donationSuccess', 'true');
  }

  const query = params.toString();
  return query
    ? `/web/embed/sticker/${stickerId}?${query}`
    : `/web/embed/sticker/${stickerId}`;
}

/** Standalone sticker embed → sticker page; org shop embed → org profile */
export function getEmbedNavigationHomeUrl(
  contextOverride?: Partial<EmbedOrganizationContext> & { donationSuccess?: boolean },
): string | null {
  const resolved = resolveEmbedOrganizationContext();
  const standalone = contextOverride?.standalone ?? resolved.standalone;
  const stickerId = contextOverride?.stickerId ?? resolved.stickerId;

  if (standalone && stickerId) {
    return getEmbedStickerHomeUrl(contextOverride);
  }

  return getEmbedOrganizationHomeUrl(contextOverride);
}

export function appendEmbedOrganizationQuery(
  pathWithQuery: string,
  routeLike?: {
    query?: Record<string, unknown>;
    fullPath?: string;
    path?: string;
  },
): string {
  const context = resolveEmbedOrganizationContext(routeLike);
  const [base, queryString = ''] = pathWithQuery.split('?');
  const params = new URLSearchParams(queryString);

  if (context.organizationId && !params.has('embedOrganizationId')) {
    params.set('embedOrganizationId', context.organizationId);
  }
  if (context.embedSource && !params.has('embedSource')) {
    params.set('embedSource', context.embedSource);
  }
  if (isEmbeddedContext() && !params.has('embedded')) {
    params.set('embedded', 'true');
  }

  const nextQuery = params.toString();
  return nextQuery ? `${base}?${nextQuery}` : base;
}

export function navigateToEmbedOrganizationHome(
  routeLike?: {
    query?: Record<string, unknown>;
    fullPath?: string;
    path?: string;
  },
  contextOverride?: Partial<EmbedOrganizationContext>,
  options?: {
    router?: { push: (path: string) => void | Promise<void> };
  },
): boolean {
  if (!isEmbeddedContext()) {
    return false;
  }

  const resolved = resolveEmbedOrganizationContext(routeLike);
  const organizationId = contextOverride?.organizationId ?? resolved.organizationId;
  const embedSource = contextOverride?.embedSource ?? resolved.embedSource;
  const stickerId = contextOverride?.stickerId ?? resolved.stickerId;
  const standalone = contextOverride?.standalone ?? resolved.standalone;
  const homeUrl = getEmbedNavigationHomeUrl({
    organizationId,
    embedSource,
    stickerId,
    standalone,
  });

  if (!homeUrl) {
    return false;
  }

  if (options?.router) {
    options.router.push(homeUrl);
  } else {
    window.location.href = homeUrl;
  }

  return true;
}

/** Re-scroll parent embed after iframe height changes (e.g. success page shrink) */
export function scrollEmbedToTopAfterHeightSync(reason = 'embed-height-settled'): void {
  if (!isEmbeddedContext()) {
    return;
  }

  scrollEmbedToTop(reason);
  window.setTimeout(() => scrollEmbedToTop(`${reason}-400`), 400);
  window.setTimeout(() => scrollEmbedToTop(`${reason}-900`), 900);
}

/**
 * Scroll the iframe document and ask the parent page to scroll to the top of the embed.
 */
export function scrollEmbedToTop(reason = 'scroll-to-top'): void {
  if (!isEmbeddedContext()) {
    return;
  }

  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  window.parent.postMessage(
    {
      type: 'spotlight-iframe-scroll-top',
      reason,
    },
    '*',
  );
}

/**
 * Ask the parent page to scroll so the vertical center of the iframe is visible.
 * Used when showing modals that are centered within the iframe document.
 */
export function scrollEmbedToCenter(reason = 'scroll-to-center'): void {
  if (!isEmbeddedContext()) {
    return;
  }

  window.parent.postMessage(
    {
      type: 'spotlight-iframe-scroll-to-center',
      reason,
    },
    '*',
  );
}
